import ClueCard from "./ClueCard";
import BuzzControls from "./BuzzControls";
import RoundStatus from "./RoundStatus";
import AnswerPhase from "./AnswerPhase";
import RoundFeedback from "./RoundFeedback";
import SessionStats from "./SessionStats";
import ReviewCompleteScreen from "./ReviewCompleteScreen";
import BackButton from "../BackButton";
import { useEffect, useRef, createContext, useState } from "react";

const READY_MS = 1000;
const BUZZER_FEEDBACK_MS = 2000;
const REVIEW_FEEDBACK_MS = 500;
const BUZZ_WINDOW_MS = 3000;

export const BuzzerStateContext = createContext(null);

function CardsHomeScreen({ state, dispatch, mode, questions, loadBoard, loadQuestions, onRatedAttempt, ratings, sessionStartRatings}) {
    const [isNarrating, setIsNarrating] = useState(false);
    const utteranceRef = useRef(null);
    const isFirefox = navigator.userAgent.includes("Firefox");

    useEffect(() => {
        if (!state.currentQuestion || state.phase === "complete") return;

        if (state.phase === "ready" && state.mode === "decision") {
            const id = window.setTimeout(() => {
                dispatch({ type: "START_READING", now: Date.now() });
            }, READY_MS);

            return () => window.clearTimeout(id);
        }

        if (state.phase === "reading") {
            const clueText = state.currentQuestion.text ?? "";

            // If clue text is empty, skip straight to buzzable
            if (!clueText.trim()) {
                dispatch({ type: "OPEN_BUZZ_WINDOW", now: Date.now() });
                return;
            }

            // Cancel any previous speech just to be safe
            window.speechSynthesis.cancel();

            let cancelled = false;

            const utterance = new SpeechSynthesisUtterance(clueText);
            utteranceRef.current = utterance;

            utterance.rate = 0.95;
            utterance.pitch = 1;
            utterance.volume = 1;

            utterance.onstart = () => {
                if (cancelled) return;

                setIsNarrating(true);
            };

            utterance.onend = () => {
                setIsNarrating(false);
                setTimeout(() => {
                    dispatch({ type: "OPEN_BUZZ_WINDOW", now: Date.now() });
                }, 0); // May need to adjust this delay if the buzz button visibly changes before the final audio finishes
            };

            utterance.onerror = () => {
                setIsNarrating(false);
                // Fallback: still move forward if speech fails
                dispatch({ type: "OPEN_BUZZ_WINDOW", now: Date.now() });
            };

            utterance.text = normalizeSpokenText(clueText);
            window.speechSynthesis.speak(utterance);

            return () => {
                utterance.onend = null;
                utterance.onerror = null;
                window.speechSynthesis.cancel();
            };
        }

        if (state.phase === "buzzable") {
            const id = window.setTimeout(() => {
                if (state.ratingEligible) {
                    onRatedAttempt(
                        state.currentQuestion,
                        0
                    );
                }

                dispatch({
                    type: "AUTO_TIMEOUT_PASS",
                });
            }, BUZZ_WINDOW_MS);

            return () => window.clearTimeout(id);
        }

        if (state.phase === "feedback") {
            const feedback_ms = state.mode === "decision" ? BUZZER_FEEDBACK_MS : REVIEW_FEEDBACK_MS;
            const id = window.setTimeout(() => {
                dispatch({ type: "ADVANCE_ROUND", now: Date.now() });
            }, feedback_ms);
            return () => window.clearTimeout(id);
        }
    }, [state.phase, state.currentQuestion, state.mode, state.ratingEligible, dispatch, onRatedAttempt]);

    useEffect(() => {
        if (state.questions.length > 0 && state.currentQuestion && state.readyStartedAt === null) {
            dispatch({ type: "START_ROUND", now: Date.now() });
        }
    }, [state.questions, state.currentQuestion, state.readyStartedAt, dispatch]);

    useEffect(() => {
        if (!state.buzzDisabled) return;

        const timer = setTimeout(() => {
            dispatch({ type: "ENABLE_BUZZ" });
        }, 250);

        return () => clearTimeout(timer);
    }, [state.buzzDisabled, dispatch]);

    function normalizeSpokenText(msg) {
        return msg.replace(/____/g, "blank") // underscores
            .replace(/THE/g, "the") // the
            .replace(/"/g, "") // quotes
            .replace(/&/g, "and") // ampersands
            .replace(/\bDr\.?\b/g, "Doctor")
            .replace(/\bAWS\b/g, "A W S")
            .replace(/\bCEO\b/g, "C E O")
            .replace(/U\.S\./g, "United States")
            .replace(/(\d+(?:\.\d+)?)\^(-?\d+)/g, "$1 to the power of $2") // Convert exponents: 10^23 -> 10 to the power of 23
            .replace(/(\d)\s*[x×]\s*(\d)/gi, "$1 times $2") // Convert multiplication between numbers: 6.022 x 10 -> 6.022 times 10
            .toLowerCase();
    }

    function getCorrectedCount(state) {
        return state.reviewResults.filter((r) => r.corrected).length;
    }

    function answerCorrect(state) {
        if (state.ratingEligible) {
            onRatedAttempt(
                state.currentQuestion,
                1
            );
        }

        if (state.mode === "decision") {
            dispatch({ type: "ANSWER_CORRECT_BUZZER" });
        } else {
            dispatch({ type: "ANSWER_CORRECT_REVIEW" })
        }
    }

    function answerIncorrect(state) {
        if (state.ratingEligible) {
            onRatedAttempt(
                state.currentQuestion,
                0
            );
        }

        if (state.mode === "decision") {
            dispatch({ type: "ANSWER_INCORRECT_BUZZER" })
        } else {
            dispatch({ type: "ANSWER_INCORRECT_REVIEW" })
        }
    }

    function handleBuzz() {
        const now = Date.now();

        if (state.buzzDisabled) {
            return;
        }

        if (state.phase === "ready" || state.phase === "reading") {
            dispatch({
                type: "BUZZ_TOO_EARLY",
                now,
            });
            return;
        }

        if (state.phase === "buzzable") {
            dispatch({
                type: "PRESS_BUZZ",
                now,
            });
        }
    }

    function handlePass() {
        if (state.ratingEligible) {
            onRatedAttempt(
                state.currentQuestion,
                0
            );
        }

        dispatch({
            type: "PRESS_PASS",
        });
    }

    function handleExit() {

        const synth = window.speechSynthesis;

        synth.cancel();

        if (synth.paused) {
            synth.resume();
        }

        dispatch({
            type: "EXIT_TO_MENU",
        });
    }

    return (
        <BuzzerStateContext.Provider value={{ state: state, dispatch: dispatch }}>
            <div>
                {mode === 'buzzer' && <div className="session-screen">
                    <div className="session-toolbar">
                        <BackButton
                            label="Exit"
                            disabled={isFirefox && isNarrating}
                            onClick={handleExit}
                        />
                    </div>

                    {state.phase !== "complete" && (
                        <div>
                            <RoundStatus mode={state.mode} phase={state.phase} round={state.currentIndex + 1} totalRounds={state.questions.length} />
                            <div className="clue-card-container">
                                <ClueCard
                                    category={state.currentQuestion?.topic ?? state.currentQuestion?.subcategory ?? state.currentQuestion?.category}
                                    clue={state.currentQuestion?.text ?? ""}
                                    answer={state.currentQuestion?.answer ?? ""}
                                    revealAnswer={state.answerRevealed &&
                                        (state.phase === "answering" ||
                                            state.phase === "feedback" ||
                                            state.mode === "review")} />

                                {state.mode === "review" && !state.answerRevealed &&
                                    <button
                                        onClick={() => dispatch({ type: "REVEAL_ANSWER" })}
                                        className="reveal-answer-button"
                                    >
                                        Reveal Answer
                                    </button>}
                            </div>
                        </div>
                    )}

                    {state.phase !== "complete" && (
                        <div className="controls-container">
                            {(state.phase === "reading" || state.phase === "buzzable") && (
                                <BuzzControls
                                    phase={state.phase}
                                    disabled={state.buzzDisabled}
                                    passDisabled={isFirefox && state.phase === "reading"}
                                    onBuzz={handleBuzz}
                                    onPass={handlePass}
                                />
                            )}

                            {state.phase === "answering" && state.answerRevealed && (
                                <AnswerPhase
                                    onCorrect={() => answerCorrect(state)}
                                    onIncorrect={() => answerIncorrect(state)}
                                />
                            )}

                            {state.phase === "feedback" && state.mode === "decision" && (
                                <div className="bottom-action-area">
                                    <RoundFeedback
                                        outcome={state.currentOutcome}
                                        scoreDelta={state.currentScoreDelta}
                                        scoreBreakdown={state.scoreFeedback}
                                        reactionMs={state.pendingReactionMs}
                                    />
                                </div>

                            )}
                        </div>
                    )}

                    {state.phase === "complete" && (
                        <div>
                            {state.mode === "decision" && (
                                <SessionStats
                                    state={state}
                                    loadQuestions={loadQuestions} />
                            )}

                            {state.mode === "review" && (
                                <ReviewCompleteScreen
                                    results={state.results}
                                    sessionOrigin={state.sessionOrigin}
                                    correctedCount={getCorrectedCount(state)}
                                    totalReviewCount={state.questions.length}
                                    onReviewAgain={() => dispatch({ type: "REVIEW_AGAIN" })}
                                    onNewStudy={() => loadQuestions("review")}
                                    onNewBuzzer={() => loadQuestions("decision")}
                                    ratings={ratings}
                                    ratingEligible={state.ratingEligible}
                                    sessionStartRatings={sessionStartRatings}
                                />
                            )}
                        </div>
                    )}
                </div>}

            </div>
        </BuzzerStateContext.Provider>
    );
}

export default CardsHomeScreen;